import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, X } from "lucide-react";

/**
 * Shows a subtle banner when the current URL contains `?notif=<id>&notif_title=<title>`.
 * Rendered globally inside AppShell/MeShell so notification deep-links always feel connected.
 */
export default function NotificationBanner() {
  const location = useLocation();
  const nav = useNavigate();
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const notifId = params.get("notif");
    const notifTitle = params.get("notif_title");
    if (notifId) {
      setBanner({ id: notifId, title: notifTitle || "You arrived from a notification" });
    } else {
      setBanner(null);
    }
  }, [location.search]);

  if (!banner) return null;

  const dismiss = () => {
    setBanner(null);
    // Strip the notif params from the URL without leaving history noise
    const params = new URLSearchParams(location.search);
    params.delete("notif"); params.delete("notif_title");
    const q = params.toString();
    nav(`${location.pathname}${q ? `?${q}` : ""}`, { replace: true });
  };

  return (
    <div
      className="mb-4 rounded-lg bg-pink/5 border-l-4 border-pink px-4 py-2.5 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300"
      data-testid="notif-banner">
      <div className="w-7 h-7 rounded-full bg-pink/10 grid place-items-center shrink-0">
        <Bell className="w-3.5 h-3.5 text-pink" />
      </div>
      <div className="flex-1 min-w-0 text-sm">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-pink mr-2">From your inbox</span>
        <span className="text-neutral-700 truncate">{banner.title}</span>
      </div>
      <button onClick={dismiss} className="text-neutral-400 hover:text-neutral-700" data-testid="notif-banner-close" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
